/*
 * "Talk to a furniture expert" module (snippets/expert-cta.liquid).
 *
 * One delegated click listener for every [data-expert-cta] on the page:
 *   1. push { event: 'expert_cta_click', contact_path, product_handle, product_id }
 *      onto window.dataLayer, for a GTM custom-event trigger to forward to GA4;
 *   2. for the chat path, open the HubSpot chat widget when its JS API is present,
 *      and otherwise let the link fall through to the Contact Us page.
 *
 * A chat click that ends on the Contact Us page instead of an open chat also pushes
 *   { event: 'expert_cta_chat_fallback', chat_fallback_reason, product_handle, product_id }
 * so the record says the visitor did not get a chat. expert_cta_click keeps
 * contact_path 'chat' for the link that was clicked. chat_fallback_reason:
 *   chat_unavailable  HubSpot decided not to show the widget (office hours, targeting)
 *   load_timeout      the widget had not loaded by CHAT_LOAD_FALLBACK_MS
 *   chat_error        HubSpot's API threw
 *   no_chat_api       no HubSpot API on the page (blocked, or not yet loaded)
 *   modified_click    the visitor chose a new tab or window; not a chat outage, so
 *                     leave it out when measuring chat availability.
 *
 * The phone and form links keep their native navigation: the push happens first,
 * and GA4 sends its hit with a beacon, so the event survives the page change.
 *
 * While chat is offline the chat link is relabelled (owner decision, Issue #4115):
 * when HubSpot's own page-load of the widget settles with nothing shown, the link's
 * text becomes its data-expert-cta-chat-offline copy. Its href, its 'chat' path and
 * the click handling above stay as they are, so a click still asks HubSpot and
 * falls back to Contact Us, and the events are unchanged.
 */
(function () {
  if (window.vwsExpertCtaBound) return;
  window.vwsExpertCtaBound = true;

  // The longest a chat click waits for the widget before falling back to the
  // Contact Us page. HubSpot normally decides far sooner (see pollChat); this
  // ceiling covers a config request that never answers.
  var CHAT_LOAD_FALLBACK_MS = 4000;
  var CHAT_STATUS_POLL_MS = 100;
  var chatLoading = false;

  // Read fresh each time: with loadImmediately off, HubSpot replaces its stub API
  // with the real one inside widget.load().
  function chatWidget() {
    var conversations = window.HubSpotConversations;
    return conversations && conversations.widget ? conversations.widget : null;
  }

  function widgetStatus(widget) {
    return (typeof widget.status === 'function' && widget.status()) || {};
  }

  function pushChatFallback(module, reason) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'expert_cta_chat_fallback',
      chat_fallback_reason: reason,
      product_handle: module.getAttribute('data-product-handle'),
      product_id: module.getAttribute('data-product-id'),
    });
  }

  // After widget.load(): open the chat once it has loaded, or call onUnavailable
  // as soon as HubSpot has decided not to show it.
  //
  // status() reports { loaded, pending } (`pending` is not in HubSpot's docs but
  // is what its embed returns). pending is true while HubSpot fetches the chat's
  // configuration and loads the iframe; on success loaded turns true in the same
  // step. Settled with nothing loaded means HubSpot hid the widget: outside the
  // chatflow's office hours (its config request answers HIDE_WIDGET,
  // OUT_OF_OFFICE_HOURS, measured 2026-10-07) or by a targeting rule. Reading the
  // hours from HubSpot keeps this in step with the chatflow's own schedule. An
  // embed that stops reporting `pending` falls back at the ceiling, as before.
  function pollChat(onUnavailable, startedAt) {
    var widget, status;
    try {
      widget = chatWidget();
      status = widget ? widgetStatus(widget) : {};
    } catch (error) {
      // A throwing API must not leave chatLoading set, which would dead-end every
      // later chat click on this page.
      chatLoading = false;
      onUnavailable('chat_error');
      return;
    }
    if (status.loaded) {
      chatLoading = false;
      widget.open();
      return;
    }
    var hidden = status.pending === false;
    if (hidden || Date.now() - startedAt >= CHAT_LOAD_FALLBACK_MS) {
      chatLoading = false;
      onUnavailable(hidden ? 'chat_unavailable' : 'load_timeout');
      return;
    }
    window.setTimeout(function () {
      pollChat(onUnavailable, startedAt);
    }, CHAT_STATUS_POLL_MS);
  }

  // Returns true when the chat was (or is being) opened in-page, so the caller
  // cancels the link's navigation; false leaves the link to the Contact Us page.
  function openHubSpotChat(onUnavailable) {
    var widget = chatWidget();
    if (!widget) return false;

    if (widgetStatus(widget).loaded) {
      widget.open();
      return true;
    }
    if (typeof widget.load !== 'function') return false;

    if (!chatLoading) {
      chatLoading = true;
      var startedAt = Date.now();
      widget.load({ widgetOpen: true });
      // First read after a tick, so a load that starts asynchronously is not
      // mistaken for one that has already settled.
      window.setTimeout(function () {
        pollChat(onUnavailable, startedAt);
      }, CHAT_STATUS_POLL_MS);
    }
    return true;
  }

  // Relabel the chat link once HubSpot's own page-load of the widget (loadImmediately,
  // its default) settles with nothing shown: outside the chatflow's office hours, or a
  // targeting rule. The label is the snippet's data-expert-cta-chat-offline copy; href,
  // path and click handling are untouched, so a click still asks HubSpot (hours may
  // have started since the page loaded) and falls back to Contact Us as above.
  //
  // Only a load SEEN pending and then settled with nothing loaded counts. A bare
  // { loaded: false, pending: false } proves nothing: HubSpot's loadImmediately-off
  // stub reads that forever (its load() installs the real API), and the live embed
  // reads it for a moment between onReady and its auto-load. A page whose HubSpot
  // had already settled before this script ran therefore keeps "Chat with us" and
  // falls back on click as before. The watch stops at the ceiling, or when the
  // widget loads.
  var CHAT_OFFLINE_WATCH_MS = 10000;

  function markChatOffline() {
    var links = document.querySelectorAll('[data-expert-cta] [data-expert-cta-path="chat"][data-expert-cta-chat-offline]');
    for (var i = 0; i < links.length; i++) {
      links[i].textContent = links[i].getAttribute('data-expert-cta-chat-offline');
    }
  }

  function watchChatAvailability(startedAt, seenPending) {
    var status;
    try {
      var widget = chatWidget();
      status = widget ? widgetStatus(widget) : {};
    } catch (error) {
      return;
    }
    if (status.loaded) return;
    if (status.pending === true) {
      seenPending = true;
    } else if (status.pending === false && seenPending) {
      markChatOffline();
      return;
    }
    if (Date.now() - startedAt >= CHAT_OFFLINE_WATCH_MS) return;
    window.setTimeout(function () {
      watchChatAvailability(startedAt, seenPending);
    }, CHAT_STATUS_POLL_MS);
  }

  function startChatAvailabilityWatch() {
    watchChatAvailability(Date.now(), false);
  }

  // HubSpot's documented ready hook: the API may land before or after this script.
  if (window.HubSpotConversations) {
    startChatAvailabilityWatch();
  } else {
    window.hsConversationsOnReady = window.hsConversationsOnReady || [];
    window.hsConversationsOnReady.push(startChatAvailabilityWatch);
  }

  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('[data-expert-cta-path]');
    if (!link) return;
    var module = link.closest('[data-expert-cta]');
    if (!module) return;

    var path = link.getAttribute('data-expert-cta-path');
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'expert_cta_click',
      contact_path: path,
      product_handle: module.getAttribute('data-product-handle'),
      product_id: module.getAttribute('data-product-id'),
    });
    if (path !== 'chat') return;

    // A modified or non-primary click (new tab, new window) keeps the browser's
    // own behaviour, which is the link's Contact Us page.
    var plainClick = event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
    if (!plainClick) {
      pushChatFallback(module, 'modified_click');
      return;
    }
    var fallbackHref = link.href;
    var openedInPage = openHubSpotChat(function (reason) {
      pushChatFallback(module, reason);
      window.location.href = fallbackHref;
    });
    if (openedInPage) {
      event.preventDefault();
    } else {
      pushChatFallback(module, 'no_chat_api');
    }
  });
})();
